<h1 mat-dialog-title>Referrals · {{ data.patientName }}</h1>

<div mat-dialog-content class="prd">
    <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>

    <div class="prd__alert" *ngIf="loadError" data-test="load-error">
        <mat-icon>error_outline</mat-icon>
        <div>{{ loadError }}</div>
    </div>

    <ng-container *ngIf="!isLoading && !loadError">

        <!-- ── This patient's referrals (§5.3, first half) ─────────────────────────────── -->
        <section class="prd__section" *ngIf="referrals.length" data-test="existing-referrals">
            <h3>Referred</h3>
            <div class="prd__referral" *ngFor="let referral of referrals; trackBy: trackById">
                <div class="prd__referral-head">
                    <span class="prd__urgency prd__urgency--{{ referral.urgency }}">{{ urgencyLabels[referral.urgency] }}</span>
                    <span class="prd__date">{{ referral.createdAt | date: 'd MMM y, HH:mm' }}</span>
                </div>
                <p class="prd__reason">{{ referral.reason }}</p>
                <p class="hint prd__future" *ngIf="referral.shareMode !== 'summary'">
                    {{ referral.futureMode === 'until' ? 'Future entries shared until ' + (referral.futureUntil | date: 'd MMM y') : futureModeLabels[referral.futureMode] }}
                </p>
                <div class="prd__sent-summary" *ngIf="referral.shareMode === 'summary' && referral.summary" data-test="sent-summary">
                    <button mat-button type="button" class="prd__sent-summary-toggle" (click)="toggleSummary(referral)">
                        <mat-icon>{{ expandedSummaryId === referral.id ? 'expand_less' : 'expand_more' }}</mat-icon>
                        Summary shared{{ referral.summary.aiAssisted ? ' (AI-assisted, reviewed)' : '' }}{{ referral.summary.fileCount ? ' with ' + referral.summary.fileCount + (referral.summary.fileCount === 1 ? ' file' : ' files') : '' }}
                    </button>
                    <pre class="prd__summary-text" *ngIf="expandedSummaryId === referral.id">{{ referral.summary.text }}</pre>
                </div>
                <div class="prd__recipient" *ngFor="let recipient of referral.recipients; trackBy: trackById" data-test="recipient-row">
                    <mat-icon class="prd__recipient-icon">person</mat-icon>
                    <div class="prd__recipient-body">
                        <div>{{ recipient.displayName || 'Invited clinician' }}<span class="hint" *ngIf="recipient.speciality"> · {{ recipient.speciality }}</span></div>
                        <div class="hint">
                            {{ statusLabel(recipient) }}
                            <span *ngIf="recipient.firstSeenAt"> {{ recipient.firstSeenAt | date: 'd MMM y' }}</span>
                            <span *ngIf="recipient.stoppedAt"> · {{ recipient.stoppedAt | date: 'd MMM y' }}</span>
                        </div>
                        <div class="hint" *ngIf="shareSummary(recipient) as summary" data-test="share-summary">
                            {{ summary }}<span *ngIf="recipient.lastSharedAt"> · last shared {{ recipient.lastSharedAt | date: 'd MMM y, HH:mm' }}</span>
                        </div>
                        <button mat-button type="button" class="prd__sent-summary-toggle" *ngIf="recipient.notSharedEntries > 0"
                            (click)="toggleUnshared(recipient)" data-test="unshared-toggle">
                            <mat-icon>{{ expandedUnsharedId === recipient.id ? 'expand_less' : 'expand_more' }}</mat-icon>
                            Entries not shared
                        </button>
                        <div class="prd__unshared" *ngIf="expandedUnsharedId === recipient.id" data-test="unshared-list">
                            <div class="hint" *ngIf="unshared[recipient.id] === 'loading'">Loading…</div>
                            <div class="hint" *ngIf="unshared[recipient.id] === 'error'">The list could not be loaded. Close it and try again.</div>
                            <ng-container *ngIf="unsharedList(recipient.id) as entries">
                                <div *ngFor="let entry of entries" data-test="unshared-entry">
                                    {{ unsharedLine(entry) }}<span class="hint"> · {{ entry.at | date: 'd MMM y' }}</span>
                                </div>
                                <div class="hint">If the recipient needs these, send them another way.</div>
                            </ng-container>
                        </div>
                    </div>
                    <!-- M6: the referral chat with this recipient (read-only, with its history, once sharing stopped). -->
                    <button mat-stroked-button *ngIf="chatEnabled && recipient.status !== 'awaiting_link' && recipient.status !== 'failed'"
                        (click)="openChat(recipient)" [disabled]="openingChatId === recipient.id" data-test="referral-chat">
                        <mat-icon>forum</mat-icon> Chat
                    </button>
                    <!-- §21.7: having shared picked entries, the practice can decide they should have the file. -->
                    <button mat-stroked-button color="primary" *ngIf="canWiden(referral, recipient)" (click)="widen(referral, recipient)"
                        [disabled]="wideningRecipientId === recipient.id" data-test="widen-referral">Share whole file</button>
                    <button mat-stroked-button *ngIf="canStop(recipient)" (click)="stopSharing(recipient)"
                        [disabled]="stoppingRecipientId === recipient.id" data-test="stop-sharing">Stop sharing</button>
                </div>
            </div>
        </section>

        <p class="hint" *ngIf="!referrals.length && !data.canRefer">This patient has not been referred.</p>

        <!-- ── A new referral (§5.3, second half) ──────────────────────────────────────── -->
        <ng-container *ngIf="data.canRefer">
            <div class="prd__empty-network" *ngIf="!options.length" data-test="no-network">
                <mat-icon>info_outline</mat-icon>
                <div>There is no one in your referral network yet. A practice administrator can invite clinicians under
                    Settings → MedicAI Network.</div>
            </div>

            <button mat-stroked-button color="primary" *ngIf="options.length && !showNewReferral"
                (click)="showNewReferral = true" data-test="start-new">
                <mat-icon>add</mat-icon> New referral
            </button>

            <section class="prd__section" *ngIf="options.length && showNewReferral" data-test="new-referral">
                <h3>New referral</h3>

                <p class="prd__label">Refer to</p>
                <div class="prd__options">
                    <mat-checkbox *ngFor="let option of options" [checked]="isSelected(option)"
                        (change)="toggleRecipient(option, $event.checked)" data-test="recipient-option">
                        {{ option.displayName }}<span class="hint" *ngIf="option.targetType === 'group'"> · MedicAI practice</span><span class="hint" *ngIf="option.speciality"> · {{ option.speciality }}</span>
                        <span class="prd__pending" *ngIf="option.status === 'pending'"> ({{ option.targetType === 'group' ? 'link request' : 'invitation' }} not yet accepted: shared when they accept)</span>
                    </mat-checkbox>
                </div>

                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Reason for referral</mat-label>
                    <input matInput [(ngModel)]="form.reason" [maxlength]="reasonMax" data-test="reason">
                    <mat-hint align="end">{{ form.reason.length }} / {{ reasonMax }}</mat-hint>
                </mat-form-field>

                <p class="prd__label">Urgency</p>
                <mat-radio-group [(ngModel)]="form.urgency" class="prd__radios">
                    <mat-radio-button *ngFor="let u of urgencies" [value]="u">{{ urgencyLabels[u] }}</mat-radio-button>
                </mat-radio-group>

                <!-- §25: what may be sent belongs to the link, not to the sender. Where every chosen recipient takes a
                     referral note and nothing else, there is no choice to make, so the form says what will be sent. -->
                <ng-container *ngIf="!isLetterOnly">
                    <p class="prd__label">What to share</p>
                    <mat-radio-group [(ngModel)]="form.shareMode" class="prd__radios prd__radios--stacked" data-test="share-mode">
                        <mat-radio-button value="full">Whole timeline</mat-radio-button>
                        <mat-radio-button value="summary">A summary of the patient file</mat-radio-button>
                    </mat-radio-group>
                </ng-container>
                <div class="prd__letter-only" *ngIf="isLetterOnly" data-test="letter-only">
                    <mat-icon>mail_outline</mat-icon>
                    <div>A referral note. A clinician outside MedicAI is sent the note, the patient's demographics,
                        allergies and diagnoses, and the files you choose. The whole patient file goes only to another
                        MedicAI practice.</div>
                </div>
                <p class="hint" *ngIf="form.shareMode === 'full'">The patient's demographics and full clinical timeline: notes,
                    admissions, returned documents and ward movements.</p>

                <ng-container *ngIf="form.shareMode === 'summary'">
                    <div class="prd__alert" *ngIf="aiGate.locked" data-test="ai-locked">
                        <mat-icon>error_outline</mat-icon>
                        <div>{{ aiLockedMessage }}</div>
                    </div>
                    <div class="prd__notice" *ngIf="aiGate.lowBalance !== null" data-test="ai-low-balance">
                        <mat-icon>warning</mat-icon>
                        <div>Your token balance is currently R{{ aiGate.lowBalance | number: '1.2-2' }}. Please recharge soon to keep using AI features.</div>
                    </div>
                    <div class="prd__alert" *ngIf="draftError" data-test="draft-error">
                        <mat-icon>error_outline</mat-icon>
                        <div>{{ draftError }}</div>
                    </div>
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Summary to share</mat-label>
                        <textarea matInput [(ngModel)]="form.summaryText" [maxlength]="summaryMax" rows="12" cdkTextareaAutosize
                            [cdkAutosizeMaxRows]="24" [readonly]="isDrafting" class="prd__summary-input" data-test="summary-text"></textarea>
                        <mat-hint *ngIf="lastDraft && !isDrafting">{{ draftNote(lastDraft) }}</mat-hint>
                        <mat-hint align="end">{{ form.summaryText.length }} / {{ summaryMax }}</mat-hint>
                    </mat-form-field>
                    <!-- As the discharge summary: once there is text, MedicAI can be told how to change it. -->
                    <mat-form-field appearance="outline" class="full-width" *ngIf="form.summaryText.trim() && !isDrafting && !aiGate.locked">
                        <textarea matInput placeholder="Instruct MedicAI" cdkTextareaAutosize rows="1" [(ngModel)]="instruction"
                            [maxlength]="instructionMax" (keydown.enter)="refineDraft($event)" data-test="summary-instruction"></textarea>
                        <button matSuffix mat-icon-button type="button" (click)="refineDraft(null)" [disabled]="!instruction.trim()" data-test="summary-refine">
                            <mat-icon>send</mat-icon>
                        </button>
                        <mat-hint>
                            Customize your output
                            <mat-icon matTooltip="You can say things like: shorter, output as paragraphs, or add more detail under a heading.">info</mat-icon>
                        </mat-hint>
                    </mat-form-field>
                    <div class="prd__summary-actions">
                        <button mat-raised-button class="ai-feature" type="button" (click)="generateDraft()" [disabled]="isDrafting || aiGate.locked"
                            matTooltip="Generate a referral summary using MedicAI" data-test="generate-draft">
                            <mat-icon svgIcon="medic_ai"></mat-icon>
                            {{ isDrafting ? 'Generating…' : (form.summaryText.trim() ? 'Regenerate' : 'Generate') }}
                        </button>
                    </div>

                    <p class="prd__label">Files to send with the summary</p>
                    <div class="prd__files" data-test="summary-files">
                        <!-- The shared gallery, as the timeline shows files: click to view, delete (it confirms) to not send it. -->
                        <app-file-gallery *ngIf="chosenGallery.length" class="prd__gallery" [fileObjects]="chosenGallery"
                            [ignoreSorting]="true" [deleteEnabled]="true" [enableEditing]="false"
                            (fileRemove)="removeGalleryFile($event)"></app-file-gallery>
                        <button mat-stroked-button type="button" (click)="chooseFiles()" data-test="choose-files">
                            <mat-icon>attach_file</mat-icon> {{ chosenFiles.length ? 'Change files' : 'Choose files from the patient file' }}
                        </button>
                        <span class="hint">Optional. Up to {{ filesMax }} images or PDFs from the timeline.</span>
                    </div>
                </ng-container>

                <ng-container *ngIf="form.shareMode === 'full'">
                    <p class="prd__label">Future entries</p>
                    <mat-radio-group [(ngModel)]="form.futureMode" class="prd__radios prd__radios--stacked" data-test="future-mode">
                        <mat-radio-button *ngFor="let m of futureModes" [value]="m">{{ futureModeLabels[m] }}</mat-radio-button>
                    </mat-radio-group>
                    <mat-form-field appearance="outline" *ngIf="form.futureMode === 'until'">
                        <mat-label>Share until</mat-label>
                        <input matInput [matDatepicker]="untilPicker" [min]="minFutureDate" [(ngModel)]="form.futureUntil" data-test="future-until">
                        <mat-datepicker-toggle matSuffix [for]="untilPicker"></mat-datepicker-toggle>
                        <mat-datepicker #untilPicker></mat-datepicker>
                    </mat-form-field>
                </ng-container>

                <div class="prd__ack" *ngIf="acknowledgement">
                    <mat-checkbox [(ngModel)]="form.acknowledged" data-test="acknowledge">
                        <span class="prd__ack-text">{{ acknowledgement.text }}</span>
                    </mat-checkbox>
                </div>

                <ul class="prd__problems" *ngIf="submitAttempted && problems.length" data-test="problems">
                    <li *ngFor="let problem of problems">{{ problem }}</li>
                </ul>

                <div class="prd__alert" *ngIf="submitError" data-test="submit-error">
                    <mat-icon>error_outline</mat-icon>
                    <div>{{ submitError }}</div>
                </div>
            </section>
        </ng-container>
    </ng-container>
</div>

<div mat-dialog-actions align="end">
    <button mat-button (click)="close()" [disabled]="isSubmitting">Close</button>
    <button mat-flat-button color="primary" *ngIf="data.canRefer && showNewReferral && options.length"
        (click)="submit()" [disabled]="isSubmitting || isDrafting" data-test="submit">
        {{ isSubmitting ? 'Sending…' : (pendingSelected.length === form.linkIds.length && form.linkIds.length ? 'Create referral' : 'Send referral') }}
    </button>
</div>
